import React, { useRef, useState } from 'react';

import { connect } from 'react-redux';
import { sortableHandle, SortableElement } from 'react-sortable-hoc';

import { TableRow, TableCell, TextField, ButtonBase, Popover } from '@mui/material';

import SortableChains from './SortableChains';

import Node from '../../../../../common/Node';
import PredicateChain from '../../../../../common/PredicateChain';
import ArrowTooltipCss from '../../../../../common/ArrowTooltipCss';
import TrplCheckbox from '../../../../../common/TrplControls/TrplCheckbox';

import UiRendererSettings from '../../../../../common/UiRendererSettings';
import UiPidsConstructor from '../../../UiPidsConstructor/UiPidsConstructor';

import { isEqual } from '../../../../../utils/lodash';

import {
  getLastRstData,
  isObjectPredicate,
  getFormsSelectValues,
  isJsonObject,
} from '../../../../../utils';

import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import DragIcon from '@mui/icons-material/DragIndicator';
import ErrorIcon from '@mui/icons-material/ErrorOutline';
import EditIcon from '@mui/icons-material/Edit';
import TrplSelect from '../../../../../common/TrplControls/TrplSelect';

const DragHandle = sortableHandle(({ classes }) => (
  <div style={{}} className={classes.rowDragHandle}>
    <DragIcon className={`drag-handle-icon ${classes.boundValueDragIcon}`} />
  </div>
));

const Restriction = SortableElement(
  ({
    data,
    jels,
    classes,
    rstData,
    settings,
    columnIndex,
    addInnerRstCb,
    removeInnerRst,
    setColumnTitle,
    onRemoveRstClick,
    openFrmEditModal,
    onRstNestedDragEnd,
    light_meta_and_ops,
    handleRendererChange,
    onShowPidsFieldChange,
    conceptRestrictionsIds,
    removePidInMultipleChains,
    switchColumnToMultipleChains,
    addNewShowPidToMultipleChains,
    onCustomLayoutColumnItemChange,
  }) => {
    const [renderSettingOpened, setRenderSettingOpened] = useState(false);
    const rendernRef = useRef(null);
    const { show_pids } = settings;

    const closeRendererPopover = () => {
      setRenderSettingOpened(false);
    };

    const rstDataIsArrayOfObjects =
      typeof rstData[0] === 'object' &&
      !Array.isArray(rstData[0]) &&
      rstData[0] !== null;

    const isCustom = rstData && rstData.type === 'custom';
    let pids, objEditableFrm, isArr, rstId, columnEditable = true;

    if (!isCustom && !rstDataIsArrayOfObjects) {
      if (Number.isInteger(rstData) || Array.isArray(rstData)) {
        pids = rstData;
      } else {
        if (rstData.pids) {
          pids = rstData.pids;
          columnEditable = rstData.hasOwnProperty('editable') ? rstData.editable : true;
        }
        objEditableFrm = rstData.obj_editable_frm || null;
      }

      if (pids) isArr = Array.isArray(pids);
      rstId = isArr ? pids[0] : pids ? pids : null;
    }

    const isThisNodeEditForm = isJsonObject(rstData) && !rstId && settings.concept;

    let firstRst, lastRst, editObjectForms;
    if (rstId && settings.concept) {
      firstRst = getLastRstData(rstId, settings.concept, light_meta_and_ops.setup_rbox_light);
      lastRst = getLastRstData(pids, settings.concept, light_meta_and_ops.setup_rbox_light);
      if (lastRst) {
        if (isObjectPredicate(lastRst)) {
          editObjectForms = getFormsSelectValues(jels, lastRst.oid, light_meta_and_ops.setup_rbox_light);
        }
      }
    } else if (isThisNodeEditForm) editObjectForms = getFormsSelectValues(jels, settings.concept, light_meta_and_ops.setup_rbox_light);

    const isCustomComponentsEqual = (compObj1, compObj2) => {
      if (compObj1.custom_component.length !== compObj2.custom_component.length) return;
      /*in case one doesn't have it, the other has an empty array*/
      if ((compObj1.custom_pid && compObj1.custom_pid.length
          || compObj2.custom_pid && compObj2.custom_pid.length)
        && !isEqual(compObj1.custom_pid, compObj2.custom_pid)) return;

      const pidsDataComp1 = [], pidsDataComp2 = [];

      for (let i = 0; i < compObj1.custom_component.length; i++) {
        const comp1 = compObj1.custom_component[i], comp2 = compObj2.custom_component[i];
        if (comp1.children.length !== comp2.children.length) return;

        for (let j = 0; j < comp1.children.length; j++) {
          const childrenRowComp1 = comp1.children[j], childrenRowComp2 = comp2.children[j];
          if (childrenRowComp1.children.length !== childrenRowComp2.children.length) return;

          for (let k = 0; k < childrenRowComp1.children.length; k++) {
            const childrenColumnComp1 = childrenRowComp1.children[k],
              childrenColumnComp2 = childrenRowComp2.children[k];
            pidsDataComp1.push(childrenColumnComp1.pids, childrenColumnComp1.type);
            pidsDataComp2.push(childrenColumnComp2.pids, childrenColumnComp2.type);
          }
        }
      }
      return isEqual(pidsDataComp1, pidsDataComp2);
    };

    const isDuplicate = show_pids.filter(rstData2 => {
      if (rstData.type === 'custom' && rstData2.type === 'custom')
        return isCustomComponentsEqual(rstData, rstData2);

      return isEqual(rstData, rstData2);
    }).length > 1;

    const columnTitle = settings.column_titles && settings.column_titles[columnIndex];

    const rstBelongToConcept = isCustom || rstDataIsArrayOfObjects || isThisNodeEditForm ? true : conceptRestrictionsIds.includes(rstId);

    const hasErrors = isDuplicate || !rstBelongToConcept;
    const errorText = !rstBelongToConcept ? "No restrictions applied" : 'This view already contains same predicate';

    const setCustomComponentChange = (key, value) => {
      onCustomLayoutColumnItemChange(columnIndex, key, value);
    };

    const handleSortStart = ({ node }) => {
      const tds = document.getElementsByClassName(`${classes.draggableItemHelper}`)[0].childNodes;
      node.childNodes.forEach((node, idx) => (tds[idx].style.width = `${node.offsetWidth}px`));
    };

    return (
      <TableRow style={{ height: 'auto', borderBottom: '1px solid ghostwhite' }} className={
        `table-edit-rst-row ${classes.rstRow} ${hasErrors && classes.mandatoryField}`
      }>
        <TableCell style={{ width: '1px', padding: '1px 0 1px 1px' }}>
          <div className={`${classes.alignCenter} ${classes.row}`}>
            <ClearIcon
              color="secondary"
              onClick={() => onRemoveRstClick(columnIndex)}
              className={`${classes.mr10} ${classes.pointer}`}
            />

            <DragHandle classes={classes} />
          </div>
        </TableCell>

        <TableCell className={`${classes.fillAvailable}`} style={{ padding: '1px 10px' }}>
        <div className={`${classes.flexRow}`} style={{ display: 'flex', justifyContent: 'space-between' }}>
          {isCustom ?
            <UiPidsConstructor
              setup_rbox_light={light_meta_and_ops.setup_rbox_light}
              custom_ancestor={rstData.custom_ancestor}
              custom_pid={rstData.custom_pid}
              form_ancestor={settings.concept}
              initial_layout={rstData.custom_component}
              isDuplicate={isDuplicate}
              callbacks={{
                setCustomComponentChange,
            }}
            />
          : rstDataIsArrayOfObjects ?
            <div className={`${classes.fillAvailable}`}>
              <SortableChains
                data={data}
                jels={jels}
                useDragHandle
                classes={classes}
                rstData={rstData}
                isDuplicate={isDuplicate}
                concept={settings.concept}
                columnIndex={columnIndex}
                onSortStart={handleSortStart}
                openFrmEditModal={openFrmEditModal}
                light_meta_and_ops={light_meta_and_ops}
                onShowPidsFieldChange={onShowPidsFieldChange}
                conceptRestrictionsIds={conceptRestrictionsIds}
                removePidInMultipleChains={removePidInMultipleChains}
                addNewShowPidToMultipleChains={addNewShowPidToMultipleChains}
                onSortEnd={({ oldIndex, newIndex }) => {
                  onRstNestedDragEnd(oldIndex, newIndex, columnIndex);
                }}
                helperClass={`${classes.draggableItemHelper}`}
              />
              <div className={`${classes.mt5} ${classes.mb5}`} style={{ marginLeft: '32px' }}>
                <PredicateChain
                  mode="edit"
                  disableChain
                  props={{
                    columnIndex: columnIndex,
                  }}
                  ancestor={settings.concept}
                  addInnerRstCb={addNewShowPidToMultipleChains}
                />
              </div>
            </div>
          :
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            {isThisNodeEditForm ? null :
              <div style={{ marginRight: '1px', width: '-webkit-fill-available' }}>
                <PredicateChain
                  index={columnIndex}
                  rst={isArr ? pids : rstId}
                  props={{
                    index: columnIndex,
                  }}
                  mode="edit"
                  ancestor={settings.concept}
                  addInnerRstCb={addInnerRstCb}
                  onRemoveRstCb={removeInnerRst}
                />
              </div>
            }

            {lastRst && lastRst.type === 'BOOLEAN' ?
              <React.Fragment>
                <div
                  ref={rendernRef}
                  className={`${classes.mr5} ${classes.pointer}`}
                  onClick={() => setRenderSettingOpened(true)}
                >
                  <strong
                    style={{
                      padding: '3px',
                      fontSize: '11px',
                      color: rstData.renderer ? '#347CFF' : '9EA1BE',
                    }}
                  > R
                  </strong>
                </div>
                <Popover
                  id={`render-popover-${lastRst.id}`}
                  anchorEl={rendernRef.current}
                  open={renderSettingOpened}
                  onClose={closeRendererPopover}
                  transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                >
                  <UiRendererSettings
                    data={lastRst}
                    path={columnIndex}
                    classes={classes}
                    component={rstData}
                    handleRendererChange={handleRendererChange}
                  />
                </Popover>
              </React.Fragment>
            : null}

            {editObjectForms ? (
              <div style={{ display: 'flex', marginTop: '1px' }}>
                <TrplSelect
                  margin="none"
                  complicatedValues
                  values={editObjectForms}
                  placeholder='Select on edit click form'
                  firstChilds={
                    <React.Fragment>
                      <ButtonBase
                        style={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                        onClick={() => {
                          openFrmEditModal(
                            true,
                            { concept: (lastRst && lastRst.oid) ? lastRst.oid : settings.concept, showPidsIndex: columnIndex }
                          );
                        }}
                        className={`${classes.btnGray} ${classes.mb5}`}
                      >
                        Create form <AddIcon className={`btn-gray-icon ${classes.btnGrayIcon}`} />
                      </ButtonBase>
                    </React.Fragment>
                  }
                  value={objEditableFrm}
                  onChange={(e) => onShowPidsFieldChange(columnIndex, 'obj_editable_frm', e.target.value, null)}
                />

                <ButtonBase
                  disabled={!Boolean(objEditableFrm)}
                  style={{
                    opacity: `${!Boolean(objEditableFrm) ? '0.5' : ''}`,
                    marginLeft: '5px',
                  }}
                >
                  <EditIcon
                    style={{ display: 'flex', opacity: '1' }}
                    className={classes.editFrmIcon}
                    onClick={() => {
                      openFrmEditModal(
                        objEditableFrm,
                        { 'concept': (lastRst && lastRst.oid) ? lastRst.oid : settings.concept, 'showPidsIndex': columnIndex }
                      );
                    }}
                  />
                </ButtonBase>

                <div className={`${classes.ml10}`} style={{ width: '220px' }}>
                  <TextField
                    fullWidth
                    InputProps={{
                      inputProps: {
                        className: `autocomplete-input ${classes.input} ${classes.mr5}`,
                      }
                    }}
                    placeholder={'Form open btn title'}
                    value={rstData.title ? rstData.title : ''}
                    onChange={(e) => onShowPidsFieldChange(columnIndex, 'title', e.target.value, null)}
                  />
                </div>
              </div>
            ) : null}

            {hasErrors &&
            <ArrowTooltipCss text={errorText}>
              <ErrorIcon
                style={{
                  color: '#DD5900',
                  marginLeft: '5px',
                }}
              />
            </ArrowTooltipCss>}
          </div>}

          {rstDataIsArrayOfObjects || isCustom || editObjectForms ? null :
            <ArrowTooltipCss text={'Add multiple chains'}>
              <AddIcon
                className={classes.pointer}
                style={{ color: '#347CFF' }}
                onClick={() => switchColumnToMultipleChains(columnIndex)}
              />
            </ArrowTooltipCss>
          }
          </div>

        </TableCell>

        <TableCell style={{ padding: '1px 1px 1px 1px' }}>
          <div className={classes.nowrap}>
            {firstRst && firstRst.sid ? (
              <Node
                isLink
                size="xs"
                nodeId={firstRst.sid}
                node={data.nodes[firstRst.sid]}
                nodes={data.nodes}
              />
            ) : null}
          </div>
        </TableCell>

        <TableCell style={{ padding: '2px 10px', width: '200px', minWidth: '80px' }}>
          <TextField
            fullWidth
            InputProps={{
              inputProps: {
                className: `autocomplete-input ${classes.input} ${classes.mr5}`,
              }
            }}
            placeholder={'Column title'}
            value={columnTitle ? columnTitle : ''}
            onChange={event => setColumnTitle(columnIndex, event.target.value)}
          />
        </TableCell>

        {isCustom || rstDataIsArrayOfObjects || isThisNodeEditForm || objEditableFrm || (lastRst && lastRst.type === 'BUTTON') ? null :
          <TableCell style={{ padding: '1px 1px 1px 20px' }}>
            <TrplCheckbox
              label="Editable"
              disableRipple
              checked={columnEditable}
              classes={{
                root: classes.checkboxGray,
                label: classes.checkboxLabel,
              }}
              onChange={(e) => {
                const value = e.target.checked === true ? 'none' : false;
                onShowPidsFieldChange(columnIndex, 'editable', value, null);
              }}
            />
          </TableCell>
        }
      </TableRow>
    );
  }
);

export default connect((state) => ({
  light_meta_and_ops: state.meta.light_meta_and_ops,
}))(Restriction);
